import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getPointListApi } from "api/point";
import { useUser } from "util/context/user_context";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  green: "#059669",
  bg: "#F7FAF7",
  red: "#DC2626",
  redBg: "#FEF2F2",
};

const FONT =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif';

const ITEMS_PER_PAGE = 10;
const PAGE_GROUP = 5;

// pointTB row → 표준화 ({kind, amount, ...})
function normalizeRow(row) {
  const add = parseInt(row?.addPoint, 10) || 0;
  const use = parseInt(row?.usePoint, 10) || 0;
  const pay = parseInt(row?.payPoint, 10) || 0;
  let kind = "etc";
  let amount = 0;
  if (use > 0) {
    kind = "use";
    amount = use;
  } else if (pay > 0) {
    kind = "pay";
    amount = pay;
  } else if (add > 0) {
    kind = "add";
    amount = add;
  }
  return {
    docId: row?.docId,
    kind, // use(사용) | pay(충전) | add(적립)
    amount,
    balance: parseInt(row?.currentPoint, 10),
    matchDocId: row?.etc || "", // 매칭 사용 시 요청 docId (신규 데이터부터 기록)
    createDate: row?.createDate || "",
  };
}

function relativeTime(s) {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return "";
}

const fmt = (n) => Number(n || 0).toLocaleString();

function StatCard({ label, value, accent }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 20px",
      }}
    >
      <div style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          marginTop: 6,
          fontSize: 24,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
        <span style={{ fontSize: 14, marginLeft: 2 }}>P</span>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 36,
        padding: "0 16px",
        borderRadius: 9999,
        border: `1px solid ${active ? C.greenDark : C.line}`,
        background: active ? C.greenDark : "#fff",
        color: active ? "#fff" : C.body,
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

const KIND_META = {
  pay: { label: "포인트 충전", icon: "＋", color: C.greenDark, bg: C.greenBg, sign: "+" },
  add: { label: "포인트 적립", icon: "＋", color: C.greenDark, bg: C.greenBg, sign: "+" },
  use: { label: "매칭 사용", icon: "－", color: C.red, bg: C.redBg, sign: "-" },
  etc: { label: "포인트 변동", icon: "·", color: C.sub, bg: "#F3F4F6", sign: "" },
};

function HistoryItem({ item }) {
  const meta = KIND_META[item.kind] || KIND_META.etc;
  const rel = relativeTime(item.createDate);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 18px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 9999,
          background: meta.bg,
          color: meta.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          fontWeight: 800,
          flexShrink: 0,
        }}
        aria-hidden
      >
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
            {meta.label}
          </span>
          {item.kind === "use" && item.matchDocId && (
            <Link
              to={`/matching_done/${item.matchDocId}`}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: C.greenDark,
                textDecoration: "none",
                background: C.greenBg,
                border: `1px solid ${C.greenBorder}`,
                padding: "3px 10px",
                borderRadius: 9999,
              }}
            >
              요청 보기 →
            </Link>
          )}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: C.sub }}>
          {item.createDate}
          {rel && ` · ${rel}`}
          {!Number.isNaN(item.balance) && ` · 잔액 ${fmt(item.balance)}P`}
        </div>
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: meta.color,
          whiteSpace: "nowrap",
        }}
      >
        {meta.sign}
        {fmt(item.amount)}P
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    minWidth: 34,
    height: 34,
    padding: "0 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: C.sub,
  };
  return (
    <div
      style={{
        marginTop: 8,
        display: "flex",
        gap: 8,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        disabled={!canPrev}
        style={{ ...btnBase, color: canPrev ? C.body : "#D1D5DB", cursor: canPrev ? "pointer" : "default" }}
      >
        ‹ 이전
      </button>
      {numbers.map((n) => {
        const active = n === page;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            style={{
              ...btnBase,
              width: 34,
              borderRadius: 9999,
              background: active ? C.greenDark : "transparent",
              color: active ? "#fff" : C.body,
              fontWeight: active ? 800 : 600,
            }}
          >
            {n}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        disabled={!canNext}
        style={{ ...btnBase, color: canNext ? C.body : "#D1D5DB", cursor: canNext ? "pointer" : "default" }}
      >
        다음 ›
      </button>
    </div>
  );
}

function SkeletonItem() {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 18px",
      }}
    >
      <div style={{ width: 44, height: 44, borderRadius: 9999, background: "#F3F4F6" }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 14, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "55%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

const ExpertPointHistoryScreen = ({ isMobile }) => {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | in(충전·적립) | use(사용)
  const [period, setPeriod] = useState("all"); // all | 1m | 3m
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!userData?.id) return;
    (async () => {
      setLoading(true);
      try {
        const list = await getPointListApi(userData.id);
        const normalized = (Array.isArray(list) ? list : [])
          .map(normalizeRow)
          .sort((a, b) => String(b.createDate).localeCompare(String(a.createDate)));
        setRows(normalized);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [userData?.id]);

  const totals = useMemo(() => {
    let charged = 0;
    let used = 0;
    rows.forEach((r) => {
      if (r.kind === "use") used += r.amount;
      else charged += r.amount;
    });
    return { charged, used };
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (tab === "in") list = list.filter((r) => r.kind === "pay" || r.kind === "add");
    if (tab === "use") list = list.filter((r) => r.kind === "use");
    if (period !== "all") {
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - (period === "1m" ? 1 : 3));
      list = list.filter((r) => {
        const d = new Date(String(r.createDate).replace(" ", "T"));
        return !Number.isNaN(d.getTime()) && d >= cutoff;
      });
    }
    return list;
  }, [rows, tab, period]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  useEffect(() => {
    setPage(1);
  }, [tab, period]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const currentItems = useMemo(
    () => filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page],
  );

  return (
    <div
      style={{
        fontFamily: FONT,
        background: C.bg,
        minHeight: isMobile ? "100%" : "calc(100vh - 72px)",
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: isMobile ? "24px 16px 60px" : "32px 20px 80px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: isMobile ? 22 : 26,
                fontWeight: 800,
                color: C.ink,
                letterSpacing: "-0.02em",
              }}
            >
              포인트 내역
            </div>
            <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
              충전·사용 내역을 확인하세요. 매칭 1건당 100P가 사용됩니다.
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/addpoint")}
            style={{
              height: 40,
              padding: "0 18px",
              borderRadius: 10,
              border: "none",
              background: C.greenDark,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            포인트 충전 +
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatCard label="현재 포인트" value={fmt(userData?.point)} accent={C.greenDark} />
          <StatCard label="누적 충전·적립" value={fmt(totals.charged)} />
          <StatCard label="누적 사용" value={fmt(totals.used)} accent={C.red} />
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <FilterChip active={tab === "all"} onClick={() => setTab("all")}>
              전체
            </FilterChip>
            <FilterChip active={tab === "in"} onClick={() => setTab("in")}>
              충전·적립
            </FilterChip>
            <FilterChip active={tab === "use"} onClick={() => setTab("use")}>
              사용
            </FilterChip>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <FilterChip active={period === "all"} onClick={() => setPeriod("all")}>
              전체 기간
            </FilterChip>
            <FilterChip active={period === "1m"} onClick={() => setPeriod("1m")}>
              1개월
            </FilterChip>
            <FilterChip active={period === "3m"} onClick={() => setPeriod("3m")}>
              3개월
            </FilterChip>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <>
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
          </>
        ) : currentItems.length === 0 ? (
          <div
            style={{
              padding: "60px 16px",
              background: "#fff",
              border: `1px dashed ${C.line}`,
              borderRadius: 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              color: C.sub,
              fontSize: 14,
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 9999,
                background: C.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 30,
              }}
              aria-hidden
            >
              💰
            </div>
            <div style={{ fontWeight: 700, color: C.ink, fontSize: 15 }}>
              내역이 없어요
            </div>
            <div style={{ fontSize: 12 }}>
              조건을 바꾸거나, 포인트를 충전해보세요.
            </div>
          </div>
        ) : (
          <>
            {currentItems.map((item) => (
              <HistoryItem key={item.docId} item={item} />
            ))}
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
};

export default ExpertPointHistoryScreen;
